import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { api, getToken, clearToken } from "@/lib/api";
import { toast } from "sonner";
import { Search, LogOut, ExternalLink, Trash2, X, RefreshCcw } from "lucide-react";

const STATUSES = ["all", "new", "contacted", "qualified", "won", "lost"];
const STATUS_LABEL = {
  new: "New", contacted: "Contacted", qualified: "Qualified", won: "Won", lost: "Lost",
};
const STATUS_DOT = {
  new: "bg-rust", contacted: "bg-amber-500", qualified: "bg-blue-500",
  won: "bg-emerald-500", lost: "bg-mute",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [activeLead, setActiveLead] = useState(null);
  const [activeAudit, setActiveAudit] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async (overrideQ, overrideStatus) => {
    setLoading(true);
    try {
      const params = {};
      const _q = overrideQ ?? q;
      const _s = overrideStatus ?? statusFilter;
      if (_q) params.q = _q;
      if (_s && _s !== "all") params.status_filter = _s;
      const [statsR, leadsR] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads", { params }),
      ]);
      setStats(statsR.data);
      setLeads(leadsR.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
        return;
      }
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    refresh();
  };

  const onLogout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const updateStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      setLeads((rows) => rows.map((r) => (r.id === lead.id ? { ...r, status } : r)));
      if (activeLead?.id === lead.id) setActiveLead({ ...activeLead, status });
      toast.success(`Marked ${STATUS_LABEL[status]}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete ${lead.business_name}? This also deletes its audit.`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      setLeads((rows) => rows.filter((r) => r.id !== lead.id));
      if (activeLead?.id === lead.id) {
        setActiveLead(null);
        setActiveAudit(null);
      }
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const openLead = async (lead) => {
    setActiveLead(lead);
    setActiveAudit(null);
    try {
      const res = await api.get(`/admin/leads/${lead.id}`);
      setActiveLead(res.data.lead);
      setActiveAudit(res.data.audit);
    } catch {
      toast.error("Could not load detail");
    }
  };

  const StatCard = ({ label, value, hint }) => (
    <div className="card-paper rounded-2xl p-5">
      <div className="mono text-[10px] uppercase tracking-wider text-mute">{label}</div>
      <div className="display text-4xl mt-2">{value}</div>
      {hint && <div className="text-xs text-ink/50 mt-1">{hint}</div>}
    </div>
  );

  const counts = useMemo(() => {
    const c = { new: 0, contacted: 0, qualified: 0, won: 0, lost: 0 };
    leads.forEach((l) => { c[l.status] = (c[l.status] || 0) + 1; });
    return c;
  }, [leads]);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <header className="px-6 lg:px-10 h-16 flex items-center justify-between border-b border-ink/10 sticky top-0 bg-cream/90 backdrop-blur z-30">
        <div className="flex items-center gap-6">
          <Logo />
          <span className="mono text-xs uppercase tracking-wider text-mute hidden sm:inline">Operator console</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refresh()}
            className="btn-ghost px-3 py-2 rounded-full text-sm inline-flex items-center gap-2"
            data-testid="admin-refresh"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
          <button
            onClick={onLogout}
            className="btn-ghost px-3 py-2 rounded-full text-sm inline-flex items-center gap-2"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 px-6 lg:px-10 py-10 max-w-[1280px] mx-auto w-full">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
          <div>
            <span className="tag mb-2">Pipeline</span>
            <h1 className="display text-4xl lg:text-5xl mt-3">
              Lead <span className="display-italic text-rust">control room</span>.
            </h1>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard label="Total leads" value={stats?.total ?? "—"} hint="all-time" />
          <StatCard label="New" value={stats?.new ?? "—"} hint="awaiting outreach" />
          <StatCard label="Qualified" value={stats?.qualified ?? "—"} hint="in pipeline" />
          <StatCard label="Won" value={stats?.won ?? "—"} hint="closed customers" />
          <StatCard label="Audits ready" value={stats?.audits_ready ?? "—"} hint="generated" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <form onSubmit={onSearch} className="flex-1 min-w-[260px] flex items-center gap-2 card-paper rounded-full px-4 py-2.5">
            <Search size={16} className="text-mute" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, business, website…"
              className="bg-transparent outline-none flex-1 text-sm"
              data-testid="admin-search-input"
            />
            {q && (
              <button type="button" onClick={() => { setQ(""); refresh("", undefined); }} className="text-mute">
                <X size={14} />
              </button>
            )}
          </form>
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); refresh(undefined, s); }}
                className={`px-3 py-1.5 rounded-full text-xs mono uppercase tracking-wider border transition ${
                  statusFilter === s ? "bg-ink text-cream border-ink" : "bg-paper text-ink/70 border-ink/15 hover:bg-ink/5"
                }`}
                data-testid={`admin-filter-${s}`}
              >
                {s} {s !== "all" && counts[s] ? `· ${counts[s]}` : ""}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="card-paper rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-xs mono uppercase tracking-wider text-mute border-b border-ink/10">
                  <th className="px-5 py-4 font-medium">Lead</th>
                  <th className="px-5 py-4 font-medium">Business</th>
                  <th className="px-5 py-4 font-medium">Industry</th>
                  <th className="px-5 py-4 font-medium">Audit</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium text-right">Created</th>
                  <th className="px-5 py-4 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-mute">Loading…</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-mute">No leads yet. They'll show up here in real-time.</td></tr>
                )}
                {!loading && leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-b border-ink/8 hover:bg-ink/[0.03] cursor-pointer"
                    onClick={() => openLead(l)}
                    data-testid={`admin-row-${l.id}`}
                  >
                    <td className="px-5 py-4">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-xs text-mute">{l.email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-medium">{l.business_name}</div>
                      <a
                        href={l.website?.startsWith("http") ? l.website : `https://${l.website}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-rust hover:underline inline-flex items-center gap-1"
                      >
                        {l.website?.replace(/^https?:\/\//, "").slice(0, 36)}
                        <ExternalLink size={10} />
                      </a>
                    </td>
                    <td className="px-5 py-4 text-ink/70">{l.industry || "—"}</td>
                    <td className="px-5 py-4">
                      <AuditBadge status={l.audit_status} />
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs ${l.status === "new" ? "bg-rust/10 text-rust" : "bg-ink/5 text-ink/80"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[l.status]}`}></span>
                        {STATUS_LABEL[l.status]}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right text-xs text-mute mono">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); removeLead(l); }}
                        className="text-mute hover:text-rust"
                        title="Delete"
                        data-testid={`admin-delete-${l.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Detail Drawer */}
      {activeLead && (
        <div className="fixed inset-0 z-50 flex" data-testid="admin-drawer">
          <div className="absolute inset-0 bg-ink/40" onClick={() => { setActiveLead(null); setActiveAudit(null); }}></div>
          <aside className="ml-auto w-full max-w-[640px] h-full bg-cream relative overflow-y-auto shadow-2xl">
            <div className="px-6 lg:px-8 py-5 border-b border-ink/10 flex items-center justify-between sticky top-0 bg-cream z-10">
              <div className="mono text-xs uppercase tracking-[0.2em] text-mute">Lead detail</div>
              <button onClick={() => { setActiveLead(null); setActiveAudit(null); }} className="text-mute hover:text-ink">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 lg:px-8 py-8">
              <h2 className="display text-3xl">{activeLead.business_name}</h2>
              <div className="text-ink/70 mt-1">{activeLead.name} · {activeLead.email}</div>

              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <Info k="Phone" v={activeLead.phone} />
                <Info k="Website" v={
                  <a href={activeLead.website?.startsWith("http") ? activeLead.website : `https://${activeLead.website}`} target="_blank" rel="noreferrer" className="text-rust hover:underline">
                    {activeLead.website}
                  </a>
                } />
                <Info k="Industry" v={activeLead.industry || "—"} />
                <Info k="Avg ticket" v={activeLead.avg_ticket || "—"} />
                {activeLead.notes && <Info k="Notes" v={activeLead.notes} full />}
              </div>

              <div className="mt-8">
                <div className="form-label">Status</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.keys(STATUS_LABEL).map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(activeLead, s)}
                      className={`px-3 py-1.5 rounded-full text-xs mono uppercase tracking-wider border ${
                        activeLead.status === s ? "bg-ink text-cream border-ink" : "bg-paper text-ink/70 border-ink/15 hover:bg-ink/5"
                      }`}
                      data-testid={`admin-set-status-${s}`}
                    >
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <div className="flex items-center justify-between">
                  <div className="form-label !mb-0">AI audit</div>
                  {activeLead.audit_id && (
                    <a
                      href={`/audit/result/${activeLead.audit_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-rust text-xs hover:underline inline-flex items-center gap-1"
                    >
                      Open public view <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                <div className="mt-3">
                  {!activeAudit && <div className="text-mute text-sm">Loading audit…</div>}
                  {activeAudit && activeAudit.status === "generating" && (
                    <div className="card-paper rounded-xl p-5 text-sm text-mute">Audit generating…</div>
                  )}
                  {activeAudit && activeAudit.status === "error" && (
                    <div className="card-paper rounded-xl p-5 text-sm text-rust">Audit failed: {activeAudit.error || "unknown error"}</div>
                  )}
                  {activeAudit && activeAudit.status === "ready" && (
                    <div className="card-paper rounded-xl p-5 space-y-4">
                      <div>
                        <div className="mono text-[10px] uppercase tracking-wider text-rust">Headline</div>
                        <div className="display text-xl mt-1">{activeAudit.headline}</div>
                      </div>
                      <p className="text-ink/75 text-sm leading-relaxed">{activeAudit.summary}</p>
                      <div className="flex gap-4 text-sm">
                        <div><span className="text-mute">Low:</span> <strong>${activeAudit.monthly_leak_low}/mo</strong></div>
                        <div><span className="text-mute">High:</span> <strong>${activeAudit.monthly_leak_high}/mo</strong></div>
                      </div>
                      {(activeAudit.recommendations || []).length > 0 && (
                        <div>
                          <div className="mono text-[10px] uppercase tracking-wider text-mute mt-3">Top recommendations</div>
                          <ul className="mt-2 space-y-1.5 list-disc list-inside text-sm text-ink/80">
                            {activeAudit.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function AuditBadge({ status }) {
  const map = {
    pending: { c: "bg-ink/5 text-ink/60", t: "Pending" },
    generating: { c: "bg-amber-500/10 text-amber-700", t: "Generating" },
    ready: { c: "bg-emerald-500/10 text-emerald-700", t: "Ready" },
    error: { c: "bg-rust/10 text-rust", t: "Error" },
  };
  const v = map[status] || map.pending;
  return <span className={`px-2 py-1 rounded-full text-[11px] mono uppercase tracking-wider ${v.c}`}>{v.t}</span>;
}

function Info({ k, v, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="form-label">{k}</div>
      <div className="text-ink">{v}</div>
    </div>
  );
}
